'use client'

import { useState } from 'react'

import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

export interface PasswordResetResult {
  authToken: string
  userId: string
  email: string
  roles: string[]
  mobileVerified: boolean
  termsAccepted: boolean
}

interface ApiResponse {
  id?: string
  _id?: string
  email: string
  auth_token?: string
  roles?: string[]
  mobile_verified?: boolean
  terms?: boolean
}

interface ErrorResponse {
  errors?: string[]
  message?: string
}

export const useSetPassword = (): {
  loading: boolean
  error: string | null
  setPassword: (resetPasswordToken: string, password: string) => Promise<PasswordResetResult | null>
} => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setPassword = async (
    resetPasswordToken: string,
    password: string,
  ): Promise<PasswordResetResult | null> => {
    setError(null)
    setLoading(true)
    try {
      const url = getV5ApiUrl('/users/passwords')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
        },
        body: JSON.stringify({
          reset_password_token: resetPasswordToken,
          password,
          frontend_app: true,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const errorData = data as ErrorResponse

        if (errorData.errors?.includes("reset password token can't be blank")) {
          throw new Error('Reset password token is required')
        }

        if (errorData.errors?.some((e) => e.toLowerCase().includes('password'))) {
          throw new Error(
            errorData.errors.find((e) => e.toLowerCase().includes('password')) ||
              'Password requirements not met',
          )
        }

        throw new Error(errorData.message || 'Password reset failed')
      }

      const result = data as ApiResponse
      const userId = String(result.id ?? result._id ?? '')

      if (!userId || !result.email) {
        throw new Error('Incomplete user data received from server.')
      }

      return {
        authToken: result.auth_token ?? '',
        userId,
        email: result.email,
        roles: result.roles ?? [],
        mobileVerified: result.mobile_verified ?? false,
        termsAccepted: result.terms ?? false,
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.')
      return null
    } finally {
      setLoading(false)
    }
  }

  return { loading, error, setPassword }
}
